import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArtworkControlDeck, Carousel, Scroller } from "../../components";
import { ArtistContext } from "../../contexts/ArtistContext";
import Sidebar from "../../components/Sidebar/Sidebar";
import staticService from "../../services/StaticService";
import "./Artist.css";

const ROLL_DURATION = 820;
const GROUP_SWAP_DELAY = 180;
const API_BASE_URL = staticService.API_BASE_URL;
const ENABLE_ARTWORK_DESCRIPTION = true;
const DEFAULT_ARTWORK_DESCRIPTION =
  "Each work in this gallery condenses gesture, rhythm, and material into a brief visual note. It is meant to be read slowly, as a small field of movement, texture, and memory rather than a fixed image alone.";

const createEmptyPreviewState = () => ({
  isActive: false,
  direction: null,
  prevGroup: null,
  currentGroup: null,
  nextGroup: null,
  incomingGroup: null,
});

const toAbsoluteMediaUrl = (url) =>
  url ? (url.startsWith("http") ? url : `${API_BASE_URL}${url}`) : null;

const getLeadMediaDescriptor = (group) => {
  const firstItem = group?.items?.[0] || null;

  if (!firstItem) {
    return {
      firstItem: null,
      mediaUrl: null,
      thumbUrl: null,
      isPhoto: false,
      isVideo: false,
      displayReadyKey: null,
      cacheKey: null,
    };
  }

  const mediaUrl = toAbsoluteMediaUrl(firstItem.url);
  const thumbUrl = toAbsoluteMediaUrl(firstItem.thumbnail_url);
  const isVideo = firstItem.type === "video";
  const isPhoto = firstItem.type === "photo" || firstItem.type === "image";
  const displayReadyKey = isVideo ? thumbUrl || mediaUrl : mediaUrl;
  const cacheKey = `${group?.group_name || "group"}:${displayReadyKey || mediaUrl || "empty"}`;

  return {
    firstItem,
    mediaUrl,
    thumbUrl,
    isPhoto,
    isVideo,
    displayReadyKey,
    cacheKey,
  };
};

// ── Preview tile for a non-active drum panel ────────────────────
const DrumPanelPreview = ({ group }) => {
  const { firstItem, mediaUrl, thumbUrl, isVideo, isPhoto } =
    getLeadMediaDescriptor(group);

  if (!group) return null;
  if (!firstItem) {
    return <div className="drum-panel-empty" aria-hidden="true" />;
  }

  return (
    <>
      {isPhoto && (
        <img
          src={mediaUrl}
          alt={group.group_name}
          className="drum-panel-media"
          loading="eager"
        />
      )}
      {isVideo && (
        <>
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={group.group_name}
              className="drum-panel-media"
              loading="eager"
            />
          ) : (
            <video
              src={mediaUrl}
              className="drum-panel-media"
              muted
              playsInline
              preload="auto"
            />
          )}
        </>
      )}
      <div className="drum-panel-veil" />
    </>
  );
};

const Artist = ({
  carouselData = {},
  errorMessage = null,
  preloadedTeamMembers,
  teamError,
  preloadedNews,
  newsError,
}) => {
  const artistContext = useContext(ArtistContext);
  const {
    groups: contextGroups,
    currentGroup,
    currentGroupIndex,
    changeGroup,
  } = artistContext;

  const scrollerRef = useRef(null);
  const currentGroupIndexRef = useRef(currentGroupIndex);
  const rollTimeoutsRef = useRef([]);
  const queuedRollCountRef = useRef(0);
  const queuedRollDirectionRef = useRef(null);
  const isRollingRef = useRef(false);
  const startNextQueuedRollRef = useRef(() => {});
  const activeTransitionIdRef = useRef(0);
  const canResolveTransitionRef = useRef(false);
  const pendingReadyMediaKeyRef = useRef(null);
  const readyLeadMediaKeysRef = useRef(new Set());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [currentCarouselItem, setCurrentCarouselItem] = useState(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  const findGroupDataByIndex = useCallback(
    (index) => {
      if (!carouselData.groups || !contextGroups || contextGroups.length === 0) {
        return null;
      }

      const totalGroups = contextGroups.length;
      const wrappedIndex = ((index % totalGroups) + totalGroups) % totalGroups;
      const name = contextGroups[wrappedIndex];
      return carouselData.groups.find((group) => group.group_name === name) || null;
    },
    [carouselData.groups, contextGroups]
  );

  const currentGroupData = useMemo(
    () => findGroupDataByIndex(currentGroupIndex),
    [currentGroupIndex, findGroupDataByIndex]
  );

  const filteredMediaItems = useMemo(
    () => currentGroupData?.items || [],
    [currentGroupData]
  );

  const prevGroupData = useMemo(
    () => findGroupDataByIndex(currentGroupIndex - 1),
    [currentGroupIndex, findGroupDataByIndex]
  );

  const nextGroupData = useMemo(
    () => findGroupDataByIndex(currentGroupIndex + 1),
    [currentGroupIndex, findGroupDataByIndex]
  );

  useEffect(() => {
    setCurrentCarouselItem(filteredMediaItems[0] || null);
    setCurrentCarouselIndex(0);
    setDescriptionOpen(false);
  }, [filteredMediaItems]);

  useEffect(() => {
    if (currentCarouselIndex !== 0) {
      setDescriptionOpen(false);
    }
  }, [currentCarouselIndex]);

  const [previewState, setPreviewState] = useState(createEmptyPreviewState);

  const sidebarLayoutVars = useMemo(
    () => ({
      "--sidebar-center-y": "55%",
      "--sidebar-height": "45vh",
      "--sidebar-height-mobile": "40vh",
    }),
    []
  );

  const resetPreviewState = useCallback((transitionId = activeTransitionIdRef.current) => {
    if (transitionId !== activeTransitionIdRef.current) {
      return;
    }

    canResolveTransitionRef.current = false;
    pendingReadyMediaKeyRef.current = null;
    isRollingRef.current = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPreviewState(createEmptyPreviewState());
        requestAnimationFrame(() => {
          startNextQueuedRollRef.current();
        });
      });
    });
  }, []);

  const markCurrentItemDisplayReady = useCallback(
    (mediaKey) => {
      if (!mediaKey) return;

      readyLeadMediaKeysRef.current.add(mediaKey);

      if (
        canResolveTransitionRef.current &&
        pendingReadyMediaKeyRef.current === mediaKey
      ) {
        resetPreviewState();
      }
    },
    [resetPreviewState]
  );

  const clearQueuedRolls = useCallback(() => {
    rollTimeoutsRef.current.forEach(clearTimeout);
    rollTimeoutsRef.current = [];
    queuedRollCountRef.current = 0;
    queuedRollDirectionRef.current = null;
    isRollingRef.current = false;
    activeTransitionIdRef.current += 1;
    canResolveTransitionRef.current = false;
    pendingReadyMediaKeyRef.current = null;
  }, []);

  useEffect(() => {
    currentGroupIndexRef.current = currentGroupIndex;
  }, [currentGroupIndex]);

  useEffect(() => clearQueuedRolls, [clearQueuedRolls]);

  const performRoll = useCallback((direction) => {
    if (!contextGroups || contextGroups.length <= 1 || isRollingRef.current) {
      return;
    }

    const totalGroups = contextGroups.length;
    const fromIndex = currentGroupIndexRef.current;
    const newIndex =
      direction === "next"
        ? (fromIndex + 1) % totalGroups
        : (fromIndex - 1 + totalGroups) % totalGroups;
    const nextCurrentGroup = findGroupDataByIndex(newIndex);
    const nextCurrentLeadMedia = getLeadMediaDescriptor(nextCurrentGroup);
    const transitionId = activeTransitionIdRef.current + 1;

    isRollingRef.current = true;
    activeTransitionIdRef.current = transitionId;
    canResolveTransitionRef.current = false;
    pendingReadyMediaKeyRef.current = nextCurrentLeadMedia.displayReadyKey;

    setPreviewState({
      isActive: true,
      direction,
      prevGroup: findGroupDataByIndex(fromIndex - 1),
      currentGroup: findGroupDataByIndex(fromIndex),
      nextGroup: findGroupDataByIndex(fromIndex + 1),
      incomingGroup:
        direction === "next"
          ? findGroupDataByIndex(fromIndex + 2)
          : findGroupDataByIndex(fromIndex - 2),
    });

    const swapTimeout = setTimeout(() => {
      if (activeTransitionIdRef.current !== transitionId) return;
      currentGroupIndexRef.current = newIndex;
      changeGroup(newIndex);
    }, GROUP_SWAP_DELAY);

    const resetTimeout = setTimeout(() => {
      if (activeTransitionIdRef.current !== transitionId) return;

      canResolveTransitionRef.current = true;

      if (
        !pendingReadyMediaKeyRef.current ||
        readyLeadMediaKeysRef.current.has(pendingReadyMediaKeyRef.current)
      ) {
        resetPreviewState(transitionId);
      }
    }, ROLL_DURATION);

    rollTimeoutsRef.current.push(swapTimeout, resetTimeout);
  }, [changeGroup, contextGroups, findGroupDataByIndex, resetPreviewState]);

  startNextQueuedRollRef.current = () => {
    if (
      isRollingRef.current ||
      queuedRollCountRef.current <= 0 ||
      !queuedRollDirectionRef.current
    ) {
      return;
    }

    queuedRollCountRef.current -= 1;
    const direction = queuedRollDirectionRef.current;

    if (queuedRollCountRef.current === 0) {
      queuedRollDirectionRef.current = null;
    }

    performRoll(direction);
  };

  const handleScroll = useCallback((direction) => {
    clearQueuedRolls();
    setPreviewState(createEmptyPreviewState());
    performRoll(direction);
  }, [clearQueuedRolls, performRoll]);

  const handleNavigationClick = (index) => {
    const currentIndex = currentGroupIndexRef.current;
    if (index === currentIndex) return;

    clearQueuedRolls();
    setPreviewState(createEmptyPreviewState());

    const direction = index > currentIndex ? "next" : "prev";
    const steps = Math.abs(index - currentIndex);
    queuedRollDirectionRef.current = direction;
    queuedRollCountRef.current = steps;
    startNextQueuedRollRef.current();
  };

  const handlePreviewPanelClick = useCallback(
    (direction, event) => {
      event.preventDefault();
      event.stopPropagation();

      if (isFullscreen || previewState.isActive) {
        return;
      }

      handleScroll(direction);
    },
    [handleScroll, isFullscreen, previewState.isActive]
  );

  const handleFullscreenToggle = (event) => {
    event.stopPropagation();
    setIsFullscreen((prev) => !prev);
  };

  const handleAudioToggle = useCallback((event) => {
    event.stopPropagation();
    setAudioEnabled((prev) => !prev);
  }, []);

  const handleDescriptionToggle = useCallback((event) => {
    event.stopPropagation();
    setDescriptionOpen((prev) => !prev);
  }, []);

  const handleCurrentCarouselItemChange = useCallback((item, index = 0) => {
    setCurrentCarouselItem(item);
    setCurrentCarouselIndex(index);
  }, []);

  const currentArtworkTitle = useMemo(() => {
    if (currentCarouselItem?.caption) {
      return currentCarouselItem.caption;
    }

    if (currentGroupData?.group_name) {
      return `${currentGroupData.group_name} artwork`;
    }

    return "Artwork note";
  }, [currentCarouselItem, currentGroupData]);

  const currentArtworkEyebrow = useMemo(() => {
    if (!currentGroupData?.group_name) {
      return "The First NonCarbon Artist";
    }

    const mediaKind =
      currentCarouselItem?.type === "video" ? "moving work" : "still work";
    return `${currentGroupData.group_name} • ${mediaKind}`;
  }, [currentCarouselItem, currentGroupData]);

  const currentArtworkHasAudio = currentCarouselItem?.type === "video";
  const showDescriptionControl = currentCarouselIndex === 0;

  return (
    <>
      <Scroller
        ref={scrollerRef}
        onScroll={handleScroll}
        sensitivity={50}
        transitionDuration={ROLL_DURATION}
        debounceDelay={60}
        enableKeyboard={true}
        enableTouch={true}
        momentum={true}
        direction="vertical"
        className={`artist-container${
          isFullscreen ? " artist-container--fullscreen" : ""
        }`}
        data-group={currentGroup}
        data-layout-mode={isFullscreen ? "fullscreen" : "drum"}
        data-transition-direction={previewState.direction || ""}
        role="region"
        aria-label="Vertical artwork scroller - swipe or scroll to navigate between collections"
        aria-describedby="scroller-instructions"
      >
        <div className="drum-wrapper">
          {!isFullscreen && !previewState.isActive && prevGroupData && (
            <button
              type="button"
              className="drum-preview-hitbox drum-preview-hitbox--prev"
              aria-label={`Go to previous group: ${prevGroupData.group_name}`}
              onClick={(event) => handlePreviewPanelClick("prev", event)}
            />
          )}

          {!isFullscreen && !previewState.isActive && nextGroupData && (
            <button
              type="button"
              className="drum-preview-hitbox drum-preview-hitbox--next"
              aria-label={`Go to next group: ${nextGroupData.group_name}`}
              onClick={(event) => handlePreviewPanelClick("next", event)}
            />
          )}

          <div className={`drum-idle${
            previewState.isActive && !isFullscreen ? " drum-idle--hidden" : ""
          }`}>
            {prevGroupData && (
              <div className="drum-panel drum-panel--prev">
                <DrumPanelPreview group={prevGroupData} />
              </div>
            )}

            <div className="drum-panel drum-panel--current">
              <ArtworkControlDeck
                enabled={ENABLE_ARTWORK_DESCRIPTION}
                showDescriptionControl={showDescriptionControl}
                descriptionOpen={descriptionOpen}
                onDescriptionToggle={handleDescriptionToggle}
                descriptionTitle={currentArtworkTitle}
                descriptionEyebrow={currentArtworkEyebrow}
                description={DEFAULT_ARTWORK_DESCRIPTION}
                showAudioControl={currentArtworkHasAudio}
                audioEnabled={audioEnabled}
                onAudioToggle={handleAudioToggle}
                isFullscreen={isFullscreen}
                onFullscreenToggle={handleFullscreenToggle}
              />
              <Carousel
                errorMessage={errorMessage}
                mediaItems={filteredMediaItems}
                className={`artist-carousel${
                  isFullscreen ? " fullscreen" : ""
                }`}
                audioEnabled={audioEnabled}
                onCurrentItemChange={handleCurrentCarouselItemChange}
                allowVerticalWheelPassthrough={isFullscreen}
                onCurrentItemDisplayReady={markCurrentItemDisplayReady}
              />
            </div>

            {nextGroupData && (
              <div className="drum-panel drum-panel--next">
                <DrumPanelPreview group={nextGroupData} />
              </div>
            )}
          </div>

          {previewState.isActive && !isFullscreen && (
            <div className={`drum-transition drum-transition--${previewState.direction}`}>
              {previewState.direction === "next" ? (
                <>
                  {previewState.prevGroup && (
                    <div className="drum-panel drum-panel--transition-prev-exit">
                      <DrumPanelPreview group={previewState.prevGroup} />
                    </div>
                  )}
                  {previewState.currentGroup && (
                    <div className="drum-panel drum-panel--transition-current-to-prev">
                      <DrumPanelPreview group={previewState.currentGroup} />
                    </div>
                  )}
                  {previewState.nextGroup && (
                    <div className="drum-panel drum-panel--transition-next-to-current">
                      <DrumPanelPreview group={previewState.nextGroup} />
                    </div>
                  )}
                  {previewState.incomingGroup && (
                    <div className="drum-panel drum-panel--transition-incoming-next">
                      <DrumPanelPreview group={previewState.incomingGroup} />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {previewState.incomingGroup && (
                    <div className="drum-panel drum-panel--transition-incoming-prev">
                      <DrumPanelPreview group={previewState.incomingGroup} />
                    </div>
                  )}
                  {previewState.prevGroup && (
                    <div className="drum-panel drum-panel--transition-prev-to-current">
                      <DrumPanelPreview group={previewState.prevGroup} />
                    </div>
                  )}
                  {previewState.currentGroup && (
                    <div className="drum-panel drum-panel--transition-current-to-next">
                      <DrumPanelPreview group={previewState.currentGroup} />
                    </div>
                  )}
                  {previewState.nextGroup && (
                    <div className="drum-panel drum-panel--transition-next-exit">
                      <DrumPanelPreview group={previewState.nextGroup} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Scroller>

      <Sidebar
        groups={contextGroups}
        currentIndex={currentGroupIndex}
        onIndexChange={handleNavigationClick}
        layoutVars={sidebarLayoutVars}
        preloadedTeamMembers={preloadedTeamMembers}
        teamError={teamError}
        preloadedNews={preloadedNews}
        newsError={newsError}
      />
    </>
  );
};

export default Artist;
